import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";

import { loadHollandResults } from "../../helpers/hollandStorage";
import {
  extractSpecialistNeedIds,
  fetchCareerGuidanceByHolland,
  fetchCompaniesBySpecialistNeeds,
} from "../../helpers/hollandApi";

import styles from "./styles/hollandCompanies.module.css";

const YANDEX_MAPS_API_KEY = "7e9247c8-4c61-409a-91c5-c49872daf93e";
const MAP_CENTER = [55.958736, 54.735152];
const MAP_ZOOM = 7;
const CLUSTER_GRID_SIZE = 64;
const MARKER_SOURCE = "holland-companies-source";
const CAREER_ITEMS_LIMIT = 100;
/** Сколько лучших SpecIds по MatchScore брать для поиска компаний */
const TOP_SPEC_IDS_LIMIT = 40;
const COMPANIES_LIMIT = 100;
const LIST_PAGE_SIZE = 10;

let ymapsLoadPromise = null;
let clustererCdnRegistered = false;

function loadYmaps3(apiKey) {
  if (window.ymaps3) {
    return window.ymaps3.ready.then(() => window.ymaps3);
  }

  if (!ymapsLoadPromise) {
    ymapsLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `https://api-maps.yandex.ru/v3/?apikey=${apiKey}&lang=ru_RU`;
      script.async = true;
      script.onload = () => {
        window.ymaps3.ready.then(() => resolve(window.ymaps3));
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  return ymapsLoadPromise;
}

async function loadClusterer(ymaps3) {
  if (!clustererCdnRegistered) {
    ymaps3.import.registerCdn("https://cdn.jsdelivr.net/npm/{package}", [
      "@yandex/ymaps3-clusterer@0.0",
    ]);
    clustererCdnRegistered = true;
  }

  return ymaps3.import("@yandex/ymaps3-clusterer");
}

function enrichCoordinatesWithSpecialistNeeds(coordinates, companiesData) {
  const specialistNeedsByCompanyId = new Map();

  companiesData?.forEach((company) => {
    specialistNeedsByCompanyId.set(company.Id, company.SpecialistNeeds || []);
  });

  return coordinates.map((group) => ({
    ...group,
    Coordinates: group.Coordinates?.map((point) => ({
      ...point,
      Companies: point.Companies?.map((company) => ({
        ...company,
        SpecialistNeeds: specialistNeedsByCompanyId.get(company.Id) || [],
      })),
    })),
  }));
}

function formatSalaryRange(salaryMin, salaryMax) {
  const format = (value) =>
    value != null ? `${Math.round(value).toLocaleString("ru-RU")} ₽` : null;

  const min = format(salaryMin);
  const max = format(salaryMax);

  if (min && max) {
    return `${min} — ${max}`;
  }

  return min || max || "";
}

function getCompanyName(company) {
  return company?.Shortname || company?.Fullname || company?.Name || "";
}

function flattenToFeatures(coordinates) {
  const features = [];
  let id = 0;

  coordinates.forEach((group) => {
    group.Coordinates?.forEach((point) => {
      if (point.GeoLon == null || point.GeoLat == null) {
        return;
      }

      features.push({
        type: "Feature",
        id,
        geometry: {
          coordinates: [point.GeoLon, point.GeoLat],
        },
        properties: {
          point,
          companiesCount: point.Companies?.length || 1,
        },
      });
      id += 1;
    });
  });

  return features;
}

function getClusterCompaniesCount(features) {
  return features.reduce(
    (sum, feature) => sum + (feature.properties.companiesCount || 1),
    0
  );
}

function getBoundsFromCoordinates(coordinatesList) {
  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;

  coordinatesList.forEach(([lon, lat]) => {
    minLon = Math.min(minLon, lon);
    maxLon = Math.max(maxLon, lon);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  });

  return [
    [minLon, minLat],
    [maxLon, maxLat],
  ];
}

function createMarkerElement(count, markerClass, countClass) {
  const el = document.createElement("div");
  el.className = markerClass;

  if (count > 1) {
    const countEl = document.createElement("span");
    countEl.className = countClass;
    countEl.textContent = String(count);
    el.appendChild(countEl);
  }

  return el;
}

function createClusterElement(count, clusterClass, countClass) {
  const el = document.createElement("div");
  el.className = clusterClass;

  const countEl = document.createElement("span");
  countEl.className = countClass;
  countEl.textContent = String(count);
  el.appendChild(countEl);

  return el;
}

function CompaniesMap({ coordinates, onSelectPoint }) {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const clustererRef = useRef(null);
  const mapApiRef = useRef(null);
  const onSelectPointRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);

  onSelectPointRef.current = onSelectPoint;

  useEffect(() => {
    if (!mapContainerRef.current) {
      return undefined;
    }

    let cancelled = false;

    const initMap = async () => {
      try {
        const ymaps3 = await loadYmaps3(YANDEX_MAPS_API_KEY);
        const { YMapClusterer, clusterByGrid } = await loadClusterer(ymaps3);

        if (cancelled || !mapContainerRef.current) {
          return;
        }

        const {
          YMap,
          YMapDefaultSchemeLayer,
          YMapFeatureDataSource,
          YMapLayer,
          YMapMarker,
        } = ymaps3;

        if (mapInstanceRef.current) {
          mapInstanceRef.current.destroy();
          mapInstanceRef.current = null;
          clustererRef.current = null;
        }

        const map = new YMap(mapContainerRef.current, {
          location: {
            center: MAP_CENTER,
            zoom: MAP_ZOOM,
          },
        });

        map
          .addChild(new YMapDefaultSchemeLayer())
          .addChild(new YMapFeatureDataSource({ id: MARKER_SOURCE }))
          .addChild(
            new YMapLayer({
              source: MARKER_SOURCE,
              type: "markers",
              zIndex: 1800,
            })
          );

        mapInstanceRef.current = map;
        mapApiRef.current = {
          YMapClusterer,
          clusterByGrid,
          YMapMarker,
        };
        setMapReady(true);
      } catch (error) {
        console.error("HollandCompanies map init error:", error);
        setMapReady(false);
      }
    };

    initMap();

    return () => {
      cancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.destroy();
        mapInstanceRef.current = null;
        clustererRef.current = null;
        mapApiRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const map = mapInstanceRef.current;
    const mapApi = mapApiRef.current;

    if (!mapReady || !map || !mapApi || !coordinates.length) {
      return undefined;
    }

    const features = flattenToFeatures(coordinates);
    if (!features.length) {
      return undefined;
    }

    const { YMapClusterer, clusterByGrid, YMapMarker } = mapApi;
    const markerClass = styles.marker;
    const markerCountClass = styles.markerCount;
    const clusterClass = styles.cluster;
    const clusterCountClass = styles.clusterCount;

    if (clustererRef.current) {
      map.removeChild(clustererRef.current);
      clustererRef.current = null;
    }

    const marker = (feature) => {
      const { point, companiesCount } = feature.properties;
      const markerEl = createMarkerElement(
        companiesCount,
        `${markerClass} ${styles.company}`,
        markerCountClass
      );
      markerEl.title =
        point.Companies?.map((company) => getCompanyName(company))
          .filter(Boolean)
          .join(", ") || "";

      markerEl.addEventListener("click", (e) => {
        e.stopPropagation();
        onSelectPointRef.current?.(point);
      });

      return new YMapMarker(
        {
          coordinates: feature.geometry.coordinates,
          source: MARKER_SOURCE,
        },
        markerEl
      );
    };

    const cluster = (coords, clusterFeatures) =>
      new YMapMarker(
        {
          coordinates: coords,
          source: MARKER_SOURCE,
          onClick() {
            const bounds = getBoundsFromCoordinates(
              clusterFeatures.map((feature) => feature.geometry.coordinates)
            );
            map.update({
              location: {
                bounds,
                duration: 300,
              },
            });
          },
        },
        createClusterElement(
          getClusterCompaniesCount(clusterFeatures),
          clusterClass,
          clusterCountClass
        )
      );

    const clusterer = new YMapClusterer({
      method: clusterByGrid({ gridSize: CLUSTER_GRID_SIZE }),
      features,
      marker,
      cluster,
    });

    map.addChild(clusterer);
    clustererRef.current = clusterer;

    return () => {
      if (clustererRef.current && mapInstanceRef.current) {
        mapInstanceRef.current.removeChild(clustererRef.current);
        clustererRef.current = null;
      }
    };
  }, [mapReady, coordinates]);

  return (
    <div className={styles.mapWrapper} onClick={() => onSelectPoint(null)}>
      <div ref={mapContainerRef} className={styles.map} />
    </div>
  );
}

export default function HollandCompaniesResults({ hollandData: hollandDataProp }) {
  const [hollandData, setHollandData] = useState(
    () => hollandDataProp || loadHollandResults()
  );
  const [tab, setTab] = useState("map");
  const [companies, setCompanies] = useState([]);
  const [coordinates, setCoordinates] = useState([]);
  const [careerItems, setCareerItems] = useState([]);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [visibleCompaniesCount, setVisibleCompaniesCount] =
    useState(LIST_PAGE_SIZE);
  const [load, setLoad] = useState(false);

  useEffect(() => {
    setHollandData(hollandDataProp || loadHollandResults());
  }, [hollandDataProp]);

  const loadResults = useCallback(async (data) => {
    if (!data?.hollandApi) {
      setCompanies([]);
      setCoordinates([]);
      setCareerItems([]);
      return;
    }

    setLoad(true);
    setVisibleCompaniesCount(LIST_PAGE_SIZE);

    try {
      const careerRes = await fetchCareerGuidanceByHolland(data.hollandApi, {
        limit: CAREER_ITEMS_LIMIT,
        offset: 0,
        type: 3,
      });
      const items = careerRes.data || [];
      setCareerItems(items);

      const specIds = extractSpecialistNeedIds(items, {
        limit: TOP_SPEC_IDS_LIMIT,
      });

      if (!specIds.length) {
        setCompanies([]);
        setCoordinates([]);
        return;
      }

      const companiesRes = await fetchCompaniesBySpecialistNeeds({
        specIds,
        limit: COMPANIES_LIMIT,
        offset: 0,
        coordinateSize: 3,
      });

      setCompanies(companiesRes.Data || []);
      setCoordinates(
        enrichCoordinatesWithSpecialistNeeds(
          companiesRes.Coordinats || [],
          companiesRes.Data || []
        )
      );
    } catch {
      setCompanies([]);
      setCoordinates([]);
      setCareerItems([]);
    } finally {
      setLoad(false);
    }
  }, []);

  useEffect(() => {
    loadResults(hollandData);
  }, [hollandData, loadResults]);

  if (!hollandData) {
    return (
      <div className={styles.empty}>
        <p>Сначала пройдите тест Холланда на первом этапе.</p>
      </div>
    );
  }

  return (
    <div className={styles.block}>
      <div className={styles.headerRow}>
        <div>
          <span className={styles.kicker}>По результатам теста</span>
          <h4 className={styles.title}>Организации с потребностью в специалистах</h4>
          <p className={styles.subtitle}>
            Код Холланда: <strong>{hollandData.hollandCode}</strong>
          </p>
        </div>
        <Link to="/fschool/holland-results" className={styles.summaryLink}>
          Полная сводка
        </Link>
      </div>

      <div className={styles.tabs}>
        <button
          type="button"
          className={`${styles.tab} ${tab === "map" ? styles.tabActive : ""}`}
          onClick={() => setTab("map")}
        >
          Карта
        </button>
        <button
          type="button"
          className={`${styles.tab} ${tab === "list" ? styles.tabActive : ""}`}
          onClick={() => setTab("list")}
        >
          Список
        </button>
      </div>

      {load ? (
        <div className={styles.loader}>
          <ClipLoader />
        </div>
      ) : tab === "list" ? (
        <div className={styles.list}>
          {companies.length > 0 ? (
            <>
              {companies.slice(0, visibleCompaniesCount).map((company) => (
                <article key={company.Id} className={styles.listCard}>
                  <Link
                    to={`/company/${company.Slug}`}
                    className={styles.listTitle}
                    target="_blank"
                  >
                    {getCompanyName(company)}
                  </Link>
                  {company.Address ? (
                    <div className={styles.listAddress}>{company.Address}</div>
                  ) : null}
                  {company.SpecialistNeeds?.length ? (
                    <div className={styles.needs}>
                      {company.SpecialistNeeds.map((need) => {
                        const salary = formatSalaryRange(
                          need.SalaryMin,
                          need.SalaryMax
                        );

                        return (
                          <div key={need.Id} className={styles.need}>
                            <div className={styles.needTitle}>{need.Specialist}</div>
                            {need.Industry ? (
                              <div className={styles.needIndustry}>
                                {need.Industry}
                              </div>
                            ) : null}
                            {salary ? (
                              <div className={styles.needSalary}>{salary}</div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </article>
              ))}
              {visibleCompaniesCount < companies.length ? (
                <button
                  type="button"
                  className={styles.showMore}
                  onClick={() =>
                    setVisibleCompaniesCount(
                      (currentCount) => currentCount + LIST_PAGE_SIZE
                    )
                  }
                >
                  Показать ещё
                </button>
              ) : null}
            </>
          ) : (
            <p className={styles.emptyText}>
              Организации с подходящими потребностями не найдены.
            </p>
          )}
        </div>
      ) : (
        <div className={styles.mapBlock}>
          {coordinates.length > 0 ? (
            <>
              <CompaniesMap
                coordinates={coordinates}
                onSelectPoint={setSelectedPoint}
              />
              {selectedPoint ? (
                <div
                  className={styles.popup}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className={styles.popupClose}
                    onClick={() => setSelectedPoint(null)}
                    aria-label="Закрыть"
                  >
                    ×
                  </button>
                  {selectedPoint.Companies?.map((comp) => (
                    <Link
                      key={comp.Id}
                      to={`/company/${comp.Slug}`}
                      className={styles.popupItem}
                      target="_blank"
                    >
                      <div className={styles.popupTitle}>
                        {getCompanyName(comp)}
                      </div>
                      {comp.Address ? (
                        <div className={styles.popupAddress}>{comp.Address}</div>
                      ) : null}
                      {comp.SpecialistNeeds?.length ? (
                        <div className={styles.popupSpecialists}>
                          <div className={styles.popupSpecialistsTitle}>
                            Требуемые специалисты
                          </div>
                          {comp.SpecialistNeeds.map((need) => {
                            const salary = formatSalaryRange(
                              need.SalaryMin,
                              need.SalaryMax
                            );

                            return (
                              <div key={need.Id} className={styles.popupNeed}>
                                <div className={styles.popupNeedSpecialist}>
                                  {need.Specialist}
                                </div>
                                {need.Industry ? (
                                  <div className={styles.popupNeedIndustry}>
                                    {need.Industry}
                                  </div>
                                ) : null}
                                {salary ? (
                                  <div className={styles.popupNeedSalary}>
                                    {salary}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </Link>
                  ))}
                </div>
              ) : null}
            </>
          ) : (
            <p className={styles.emptyText}>
              На карте нет организаций с подходящими потребностями.
            </p>
          )}
        </div>
      )}

      {careerItems.length > 0 ? (
        <p className={styles.hint}>
          Подбор выполнен по {careerItems.length} карьерным связям, соответствующим
          профилю теста.
        </p>
      ) : null}
    </div>
  );
}
